/**
 * The repo's first icons (one-window-polish ticket 04).
 *
 * Until now every icon in the product was a Unicode glyph inside a text label —
 * `● Record`, `❚❚ Pause`, `■ Stop` — which was the right call while the
 * transport was a labelled row: the glyph was decoration on a button that said
 * what it did in words. It stops being the right call once the words go. A glyph
 * is a character, so it cannot be sized, aligned or coloured independently of
 * the label it sits in, its metrics differ per font, and `⚙` in particular
 * resolves down a different Windows fallback path on every machine.
 *
 * **Hand-written shapes rather than a dependency.** An icon package is an answer
 * to "where do icons come from", and this codebase currently has none — so adding
 * one would be adding a second answer's worth of surface for shapes that are a
 * rectangle, two rectangles, a rectangle, a cog and an arrow.
 *
 * That paragraph used to say "if a fifth and sixth arrive, that is the moment to
 * reconsider". The fifth has arrived (`BackToTopIcon`, scroll-and-bar ticket 01)
 * and the answer is still no, because the count was never the real test — the cog
 * was already the only shape here that took any drawing, and the arrow did not.
 * **The trigger is an icon nobody can draw from primitives**, not a number.
 *
 * Three rules hold across all four:
 *
 * - **One 24×24 viewBox.** The shapes are drawn to a shared grid, so they sit on
 *   the same optical centre and the same visual weight at whatever size CSS
 *   renders them at. Nothing here sets `width` or `height`: the size is the
 *   stylesheet's business (`.transport button svg`), which is the whole reason
 *   for leaving glyphs behind.
 * - **`fill="currentColor"`.** The colour is the button's `color`, inherited —
 *   so the record button being the one coloured control in the row stays a fact
 *   about one CSS rule rather than a fact repeated in the markup.
 * - **`aria-hidden`.** Every one of these is inside a button that carries an
 *   `aria-label`; an accessible name on the icon as well would be the second
 *   half of a double announcement.
 */

/** The shared frame: the viewBox, the fill and the `aria-hidden`, in one place
 * so four icons cannot drift on any of the three. */
function Icon({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      {children}
    </svg>
  );
}

/**
 * Record: a rounded square, not a circle.
 *
 * A circle is the older convention, and the square is what was asked for — but
 * it is also the better shape here, because it is the same shape as the stop.
 * Two squares that differ only in colour say *armed* and *rolling* about one
 * take; a circle beside a square would say they were two unrelated things. The
 * corner radius is what keeps it from being mistaken for the stop at a glance.
 */
export function RecordIcon(): JSX.Element {
  return (
    <Icon>
      <rect x="4" y="4" width="16" height="16" rx="4" />
    </Icon>
  );
}

/** Pause: two bars, on the same 16-wide footprint as the two squares, so the
 * button's contents do not change size when a take starts. */
export function PauseIcon(): JSX.Element {
  return (
    <Icon>
      <rect x="5" y="4" width="5" height="16" rx="1.5" />
      <rect x="14" y="4" width="5" height="16" rx="1.5" />
    </Icon>
  );
}

/** Stop: the square, sharper-cornered than the record's so the two are
 * distinguishable in monochrome — which is what they are whenever a stop is
 * live, since only the record button is coloured. */
export function StopIcon(): JSX.Element {
  return (
    <Icon>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
    </Icon>
  );
}

/**
 * Back to top: an arrow into a bar, the `⤒` this replaces (scroll-and-bar ticket
 * 01).
 *
 * The bar matters. A bare up-arrow is "scroll up a bit", and this control does not
 * do that — it puts your *reading position* back at the first word and takes the
 * follower's window with it. The bar is what says the arrow travels all the way and
 * stops, which is the distinction the scrollbar arriving in ticket 03 makes visible
 * for the first time: dragging the thumb to the top and pressing this are now two
 * different acts.
 *
 * Drawn on the same 4–20 footprint as the four transport shapes, so it carries the
 * same visual weight beside them even though it is never rendered beside them.
 */
export function BackToTopIcon(): JSX.Element {
  return (
    <Icon>
      <rect x="5" y="4" width="14" height="2.4" rx="1.2" />
      <polygon points="12,8.4 17.6,14.6 6.4,14.6" />
      <rect x="10" y="13.4" width="4" height="6.6" rx="1" />
    </Icon>
  );
}

/**
 * Settings: a cog, and the one shape here that is not rectangles.
 *
 * Eight teeth on a 45° pitch, each tooth an arc at radius 11 spanning ±11° of
 * its centre, dropping on straight flanks to a root arc at radius 7.4 spanning
 * ±6.5° of the gap — so the teeth taper outwards and read as teeth rather than
 * as a scalloped disc. The hub is a second subpath, and `fillRule="evenodd"` is
 * what turns it into a hole rather than a second disc: a fill rule is per-path,
 * so the whole cog has to be one `d` for the middle to be knocked out of it.
 *
 * Defined here and wired up in ticket 05, which is where the settings modal it
 * opens starts existing.
 */
export function SettingsIcon(): JSX.Element {
  return (
    <Icon>
      <path
        fillRule="evenodd"
        d={
          "M9.9 1.2 A11 11 0 0 1 14.1 1.2 L14.04 4.89 A7.4 7.4 0 0 1 15.59 5.53" +
          "L18.15 2.88 A11 11 0 0 1 21.12 5.85 L18.47 8.41 A7.4 7.4 0 0 1 19.11 9.96" +
          "L22.8 9.9 A11 11 0 0 1 22.8 14.1 L19.11 14.04 A7.4 7.4 0 0 1 18.47 15.59" +
          "L21.12 18.15 A11 11 0 0 1 18.15 21.12 L15.59 18.47 A7.4 7.4 0 0 1 14.04 19.11" +
          "L14.1 22.8 A11 11 0 0 1 9.9 22.8 L9.96 19.11 A7.4 7.4 0 0 1 8.41 18.47" +
          "L5.85 21.12 A11 11 0 0 1 2.88 18.15 L5.53 15.59 A7.4 7.4 0 0 1 4.89 14.04" +
          "L1.2 14.1 A11 11 0 0 1 1.2 9.9 L4.89 9.96 A7.4 7.4 0 0 1 5.53 8.41" +
          "L2.88 5.85 A11 11 0 0 1 5.85 2.88 L8.41 5.53 A7.4 7.4 0 0 1 9.96 4.89 Z" +
          "M12 8.6 A3.4 3.4 0 1 0 12 15.4 A3.4 3.4 0 1 0 12 8.6 Z"
        }
      />
    </Icon>
  );
}
